import React, { useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import XLSX from 'xlsx';
import HttpService from '../services/httpService';
import { setLoading, setSnackInfo } from '../redux/actions/appAction';

/** Match HR racksheet hierarchy tables (black cell borders) */
const ROSTER_TABLE_HEADER_BG = '#1e3a8a';
const ROSTER_TABLE_HEADER_FG = '#ffffff';
const ROSTER_CELL_BORDER_COLOR = '#000000';
const ROSTER_CELL_BORDER = `0.5px solid ${ROSTER_CELL_BORDER_COLOR}`;
const rosterTableHeaderCell = {
  backgroundColor: ROSTER_TABLE_HEADER_BG,
  color: ROSTER_TABLE_HEADER_FG,
  fontWeight: 700,
  border: ROSTER_CELL_BORDER,
  borderColor: ROSTER_CELL_BORDER_COLOR,
};
const rosterTableBodyCell = {
  border: ROSTER_CELL_BORDER,
  borderColor: ROSTER_CELL_BORDER_COLOR,
};

/** Handle array or wrapped `{ data: [] }` / `{ departments: [] }` from department/all */
function parseDepartmentsFromResponse(deptRes) {
  const raw = deptRes?.data;
  let list = [];
  if (Array.isArray(raw)) list = raw;
  else if (raw && Array.isArray(raw.data)) list = raw.data;
  else if (raw && Array.isArray(raw.departments)) list = raw.departments;
  return list
    .map((d) => ({
      _id: d._id ?? d.id ?? d.departmentId ?? d.name,
      name: String(d.name ?? d.departmentName ?? d.title ?? '').trim(),
    }))
    .filter((d) => d.name);
}

function parseKekaRolesFromResponse(rolesRes) {
  const raw = rolesRes?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.data)) return raw.data;
  if (raw && Array.isArray(raw.roles)) return raw.roles;
  return [];
}

function parseKekaListFromResponse(res) {
  const raw = res?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.data)) return raw.data;
  return [];
}

function getRoleDepartmentName(role) {
  const v =
    role?.kekaDepartmentId?.name ??
    role?.kekaDepartmentId?.departmentName ??
    role?.department?.name ??
    role?.departmentName ??
    role?.department ??
    '';
  return String(v || '').trim();
}

function getEcCityName(ec) {
  const v =
    ec?.kekaCityId?.name ??
    ec?.city?.name ??
    ec?.cityName ??
    ec?.city ??
    '';
  return String(v || '').trim();
}

function dedupeDepartmentsByName(opts) {
  const m = new Map();
  for (const d of opts || []) {
    const name = String(d.name || '').trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (!m.has(key)) m.set(key, { _id: d._id ?? name, name });
  }
  return Array.from(m.values()).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
}

/** Union of master departments and any department string already on roster rows */
function mergeDepartmentsFromRosterRows(apiDepartments, rosterRows) {
  const merged = [...(apiDepartments || [])];
  for (const item of rosterRows || []) {
    const n = String(item?.department || '').trim();
    if (!n) continue;
    merged.push({ _id: n, name: n });
  }
  return dedupeDepartmentsByName(merged);
}

const ROSTER_STATUS_DATE_FIELD_KEYS = [
  'openReqDate',
  'backgroundVerificationFailedDate',
  'relievedDate',
  'transferredDate',
  'terminatedDate',
  'maternityDate',
  'industryDate',
  'abscondDate',
  'resignedDate',
  'warningDate',
  'bufferIddDate',
  'activeTrainingDate',
  'pipDate',
];

function emptyRosterStatusDates() {
  return ROSTER_STATUS_DATE_FIELD_KEYS.reduce((acc, key) => {
    acc[key] = '';
    return acc;
  }, {});
}

const defaultRosterForm = {
  department: '',
  city: '',
  ec: '',
  role: '',
  empId: '',
  name: '',
  emailId: '',
  joiningDate: '',
  status: '',
  ...emptyRosterStatusDates(),
  recruiter: '',
  statusNote: '',
  needsReview: '',
  l1ManagerName: '',
  l1ManagerEmailId: '',
  l1ManagerDesignation: '',
  l2ManagerName: '',
  l2ManagerEmailId: '',
  l2ManagerDesignation: '',
  l3ManagerName: '',
  l3ManagerEmailId: '',
  l3ManagerDesignation: '',
  l4ManagerName: '',
  l4ManagerEmailId: '',
  l4ManagerDesignation: '',
  l5ManagerName: '',
  l5ManagerEmailId: '',
  l5ManagerDesignation: '',
};

const rosterFields = [
  { key: 'status', label: 'Status' },
  { key: 'empId', label: 'EMP ID' },
  { key: 'name', label: 'Name' },
  { key: 'emailId', label: 'Email ID' },
  { key: 'department', label: 'Department' },
  { key: 'role', label: 'Role' },
  { key: 'city', label: 'City' },
  { key: 'ec', label: 'EC' },
  { key: 'statusNote', label: 'Status Note' },
  { key: 'recruiter', label: 'Recruiter Name' },
  { key: 'needsReview', label: 'Needs Review' },
  { key: 'l1ManagerName', label: 'L1 Manager Name' },
  { key: 'l1ManagerEmailId', label: 'L1 Manager Email ID' },
  { key: 'l1ManagerDesignation', label: 'L1 Manager Role' },
  { key: 'l2ManagerName', label: 'L2 Manager Name' },
  { key: 'l2ManagerEmailId', label: 'L2 Manager Email ID' },
  { key: 'l2ManagerDesignation', label: 'L2 Manager Role' },
  { key: 'l3ManagerName', label: 'L3 Manager Name' },
  { key: 'l3ManagerEmailId', label: 'L3 Manager Email ID' },
  { key: 'l3ManagerDesignation', label: 'L3 Manager Role' },
  { key: 'l4ManagerName', label: 'L4 Manager Name' },
  { key: 'l4ManagerEmailId', label: 'L4 Manager Email ID' },
  { key: 'l4ManagerDesignation', label: 'L4 Manager Role' },
  { key: 'l5ManagerName', label: 'L5 Manager Name' },
  { key: 'l5ManagerEmailId', label: 'L5 Manager Email ID' },
  { key: 'l5ManagerDesignation', label: 'L5 Manager Role' },
];

/** Excel upload template column headers (row 1 only) */
const ROSTER_EXCEL_TEMPLATE_HEADERS = [
  'Department',
  'City',
  'EC',
  'Role',
  'Name',
  'Email ID',
  'Joining Date',
  'Status',
  'Status Note',
  'Recruiter Name',
  'Needs Review',
  'L1 Manager Name',
  'L1 Manager Email ID',
  'L1 Manager Designation',
  'L2 Manager Name',
  'L2 Manager Email ID',
  'L2 Manager Role',
  'L3 Manager Name',
  'L3 Manager Email ID',
  'L3 Manager Role',
  'L4 Manager Name',
  'L4 Manager Email ID',
  'L4 Manager Role',
  'L5 Manager Name',
  'L5 Manager Email ID',
  'L5 Manager Role',
];

function downloadRosterExcelTemplate() {
  const ws = XLSX.utils.aoa_to_sheet([ROSTER_EXCEL_TEMPLATE_HEADERS]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Roster');
  XLSX.writeFile(wb, 'Roster_Template.xlsx');
}

function isRosterLastGroupStatus(statusText) {
  const s = String(statusText || '').toLowerCase().replace(/\s+/g, ' ').trim();
  return (
    s.includes('open req') ||
    s.includes('yet to join') ||
    (s.includes('buffer') && s.includes('idd')) ||
    s.includes('buffer(idd)')
  );
}

function sortRosterRowsForDisplay(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const primary = [];
  const lastGroup = [];
  for (const row of list) {
    if (isRosterLastGroupStatus(row?.status)) lastGroup.push(row);
    else primary.push(row);
  }
  return [...primary, ...lastGroup];
}

function normalizeRosterStatus(status) {
  return String(status || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

function isRosterOpenReqStatus(status) {
  return normalizeRosterStatus(status) === 'open req';
}

function isRosterStandardFlowStatus(status) {
  const s = normalizeRosterStatus(status);
  return (
    s === 'open req' ||
    s === 'yet to join' ||
    s === 'active'
  );
}

function isRosterJoiningDateOnlyStatus(status) {
  const s = normalizeRosterStatus(status);
  return s === 'yet to join' || s === 'active';
}

function isRosterNameOptionalStatus(status) {
  const s = normalizeRosterStatus(status);
  return s === 'open req' || s === 'yet to join';
}

const ROSTER_ENSURED_STATUS_NAMES = [
  'Open Req',
  'Yet to Join',
  'Active',
  'Background Verification Failed',
  'Deactive',
];

function mergeHrStatusOptionsForRoster(apiList, currentStatus) {
  const merged = [...(Array.isArray(apiList) ? apiList : [])];
  const existingKeys = new Set(
    merged
      .map((item) => String(item?.status || '').trim().toLowerCase())
      .filter(Boolean)
  );

  for (const status of ROSTER_ENSURED_STATUS_NAMES) {
    const key = status.toLowerCase();
    if (!existingKeys.has(key)) {
      merged.push({ _id: `roster-status-${key}`, status });
      existingKeys.add(key);
    }
  }

  const current = String(currentStatus || '').trim();
  if (current) {
    const currentKey = current.toLowerCase();
    if (!existingKeys.has(currentKey)) {
      merged.push({ _id: `roster-status-current-${currentKey}`, status: current });
    }
  }

  return merged.sort((a, b) =>
    String(a?.status || '').localeCompare(String(b?.status || ''), undefined, {
      sensitivity: 'base',
    })
  );
}

function resolveRosterStatusSelectValue(status, options) {
  const trimmed = String(status || '').trim();
  if (!trimmed) return '';
  const hit = (options || []).find(
    (item) =>
      String(item?.status || '').trim().toLowerCase() === trimmed.toLowerCase()
  );
  return hit ? hit.status : trimmed;
}

function getRosterStatusDateFieldKey(status) {
  const s = normalizeRosterStatus(status);
  if (s === 'open req') return 'openReqDate';
  if (s === 'background verification failed') return 'backgroundVerificationFailedDate';
  if (s === 'relieved') return 'relievedDate';
  if (s === 'transferred') return 'transferredDate';
  if (s === 'terminated') return 'terminatedDate';
  if (s === 'maternity') return 'maternityDate';
  if (s === 'industry') return 'industryDate';
  if (s === 'abscond') return 'abscondDate';
  if (s === 'resigned') return 'resignedDate';
  if (s === 'warning') return 'warningDate';
  if ((s.includes('buffer') && s.includes('idd')) || s.includes('buffer(idd)')) {
    return 'bufferIddDate';
  }
  if (s === 'active - training' || (s.includes('active') && s.includes('training'))) {
    return 'activeTrainingDate';
  }
  if (s === 'pip') return 'pipDate';
  return null;
}

function clearRosterStatusDates(form) {
  const next = { ...form };
  for (const key of ROSTER_STATUS_DATE_FIELD_KEYS) {
    next[key] = '';
  }
  return next;
}

function requiresRosterStatusDate(status) {
  return Boolean(getRosterStatusDateFieldKey(status));
}

function getRosterStatusDateLabel(status) {
  const trimmed = String(status || '').trim();
  if (!trimmed) return 'Status Date';
  return `${trimmed} Date`;
}

function formatRosterDateForInput(value) {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const parsed = value instanceof Date ? value : new Date(s);
  if (Number.isNaN(parsed.getTime())) return s.split('T')[0] || '';
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatRosterDateForDisplay(value) {
  const input = formatRosterDateForInput(value);
  if (!input) return '-';
  const [y, m, day] = input.split('-').map(Number);
  if (!y || !m || !day) return input;
  return `${day}/${m}/${y}`;
}

function formatEmpIdForDisplay(empId) {
  if (empId == null || empId === '' || empId === 0 || empId === '0') return '-';
  return empId;
}

function resolveRosterStatusDateFromRow(row) {
  const fieldKey = getRosterStatusDateFieldKey(row?.status);
  if (!fieldKey || !row) return null;
  return row[fieldKey] ?? null;
}

const ROSTER_PAGE_SIZE_OPTIONS = [100, 200, 300, 400, 500];

/** Free-text filters (rendered as plain TextFields). City/EC/Department/Role are
 * rendered as dropdowns sourced from KekaCity/KekaExperienceCenter/KekaDepartment/KekaRole. */
const ROSTER_FILTER_FIELDS = [
  { key: 'empId', label: 'EMP ID', placeholder: 'e.g. EMP123' },
  { key: 'name', label: 'Name', placeholder: 'e.g. John Doe' },
  { key: 'emailId', label: 'Email ID', placeholder: 'e.g. john@decorpot.com' },
];

const DEFAULT_ROSTER_FILTERS = {
  empId: '',
  name: '',
  emailId: '',
  role: '',
  department: '',
  city: '',
  ec: '',
  status: '',
};

function parseRosterListResponse(payload) {
  if (!payload || typeof payload !== 'object') return [];
  const raw = payload.data !== undefined ? payload.data : payload;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.rows)) return raw.rows;
  if (Array.isArray(raw?.list)) return raw.list;
  return [];
}

function parseRosterTotalCount(payload, listLength, pageNum, limit) {
  if (!payload || typeof payload !== 'object') {
    return listLength;
  }
  const asNum = (v) => {
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
      const n = parseInt(v, 10);
      return Number.isNaN(n) ? null : n;
    }
    return null;
  };
  const candidates = [
    payload.pagination?.totalCount,
    payload.pagination?.total,
    payload.totalCount,
    payload.total,
    payload.data?.totalCount,
    payload.data?.total,
    payload.meta?.total,
  ];
  for (const c of candidates) {
    const n = asNum(c);
    if (n != null && n >= 0) return n;
  }
  if (listLength < limit) {
    return (pageNum - 1) * limit + listLength;
  }
  return pageNum * limit + 1;
}

function Roster(props) {
  const userDetails = useSelector((state) => state.user?.userDetails ?? {});
  const [uploading, setUploading] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openDeactivateModal, setOpenDeactivateModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRosterId, setEditingRosterId] = useState('');
  const [deletingRosterId, setDeletingRosterId] = useState('');
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [filterInputs, setFilterInputs] = useState(DEFAULT_ROSTER_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_ROSTER_FILTERS);
  const [rosterForm, setRosterForm] = useState(defaultRosterForm);
  const [apiDepartments, setApiDepartments] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [allExperienceCenterOptions, setAllExperienceCenterOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [hrStatusOptions, setHrStatusOptions] = useState([]);
  const currentUserId = userDetails?._id || '';

  const departmentOptions = useMemo(
    () => mergeDepartmentsFromRosterRows(apiDepartments, rows),
    [apiDepartments, rows]
  );

  const displayRows = useMemo(() => sortRosterRowsForDisplay(rows), [rows]);

  const hrStatusOptionsForForm = useMemo(
    () => mergeHrStatusOptionsForRoster(hrStatusOptions, rosterForm.status),
    [hrStatusOptions, rosterForm.status]
  );

  const fetchRosterList = async (
    pageNum = page,
    limitNum = rowsPerPage,
    filters = appliedFilters
  ) => {
    props.setLoading(true);
    try {
      const params = {
        page: pageNum,
        limit: limitNum,
      };
      Object.keys(DEFAULT_ROSTER_FILTERS).forEach((key) => {
        const v = String(filters?.[key] || '').trim();
        if (v) params[key] = v;
      });
      const response = await HttpService.getAllRoster(params);
      const payload = response?.data;
      const list = parseRosterListResponse(payload);
      setRows(list);
      setTotalCount(parseRosterTotalCount(payload, list.length, pageNum, limitNum));
    } catch (error) {
      console.error('Error fetching roster list:', error);
      props.setSnackInfo(
        error?.response?.data?.message || 'Error fetching roster data',
        'error'
      );
      setRows([]);
      setTotalCount(0);
    } finally {
      props.setLoading(false);
    }
  };

  useEffect(() => {
    void fetchRosterList(page, rowsPerPage, appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, appliedFilters]);

  const handleFilterInputChange = (key) => (event) => {
    setFilterInputs((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleFilterSubmit = (event) => {
    event.preventDefault();
    setAppliedFilters({ ...filterInputs });
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilterInputs(DEFAULT_ROSTER_FILTERS);
    setAppliedFilters(DEFAULT_ROSTER_FILTERS);
    setPage(1);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const loadRosterMasterData = async () => {
    try {
      const [deptRes, cityRes, ecRes, rolesRes, hrStatusRes] = await Promise.all([
        HttpService.getKekaDepartments(),
        HttpService.getKekaCities(),
        HttpService.getKekaExperienceCenters(),
        HttpService.getKekaRoles(),
        HttpService.getHrStatusColorCodes(),
      ]);

      const departments = dedupeDepartmentsByName(parseDepartmentsFromResponse(deptRes));
      setApiDepartments(departments);

      const cities = parseKekaListFromResponse(cityRes).map((c) => ({
        _id: c._id,
        name: c.name,
      }));
      setCityOptions(cities);

      const experienceCenters = parseKekaListFromResponse(ecRes).map((ec) => ({
        _id: ec._id,
        name: ec.name,
        kekaCityId: ec.kekaCityId,
      }));
      setAllExperienceCenterOptions(experienceCenters);

      const roles = parseKekaRolesFromResponse(rolesRes);
      setRoleOptions(roles);

      const hrList = Array.isArray(hrStatusRes?.data) ? hrStatusRes.data : [];
      setHrStatusOptions(hrList);
    } catch (error) {
      console.error('Error loading roster master data:', error);
      props.setSnackInfo(
        error?.response?.data?.message || 'Error loading form options',
        'error'
      );
    }
  };

  useEffect(() => {
    loadRosterMasterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFieldChange = (key, value) => {
    setRosterForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleDepartmentChange = (departmentName) => {
    setRosterForm((prev) => {
      const next = { ...prev, department: departmentName };
      const currentRoleName = String(prev.role || '').trim();
      if (!currentRoleName) return next;

      const roleObj = roleOptions.find(
        (r) => String(r?.name || '').trim() === currentRoleName
      );
      const roleDeptName = getRoleDepartmentName(roleObj);
      if (roleDeptName && roleDeptName !== String(departmentName || '').trim()) {
        next.role = '';
      }
      return next;
    });
  };

  const handleCityChange = (cityName) => {
    setRosterForm((prev) => {
      const next = { ...prev, city: cityName, ec: '' };
      const currentEcName = String(prev.ec || '').trim();
      if (!currentEcName) return next;

      const ecObj = allExperienceCenterOptions.find(
        (ec) => String(ec?.name || '').trim() === currentEcName
      );
      const ecCityName = getEcCityName(ecObj);
      if (ecCityName && ecCityName !== String(cityName || '').trim()) {
        next.ec = '';
      }
      return next;
    });
  };

  const handleStatusChange = (value) => {
    setRosterForm((prev) => {
      let next = clearRosterStatusDates({ ...prev, status: value });
      if (isRosterOpenReqStatus(value)) {
        next.name = '';
        next.emailId = '';
      } else if (isRosterStandardFlowStatus(value)) {
        next.recruiter = '';
      } else {
        next.recruiter = '';
      }
      return next;
    });
  };

  const requiredLabel = (field) => {
    const ns = String(rosterForm.status || '').trim().toLowerCase();
    const isOpenReq = ns === 'open req';

    if (field.key === 'recruiter') {
      return isOpenReq ? `${field.label} *` : field.label;
    }
    if (field.key === 'emailId') {
      if (isOpenReq) return field.label;
    }

    const isRequired =
      [
        'department',
        'city',
        'ec',
        'role',
        'status',
        'l1ManagerName',
        'l1ManagerEmailId',
        'l1ManagerDesignation',
      ].includes(field.key) ||
      (field.key === 'emailId' && !isOpenReq) ||
      (field.key === 'name' && !isRosterNameOptionalStatus(ns));
    return isRequired ? `${field.label} *` : field.label;
  };

  const renderRosterFormField = (field) => {
    const ns = String(rosterForm.status || '').trim().toLowerCase();
    const isOpenReq = ns === 'open req';
    const statusDateFieldKey = getRosterStatusDateFieldKey(rosterForm.status);
    const needsStatusDate = Boolean(statusDateFieldKey);

    const label = requiredLabel(field);
    const commonProps = {
      label,
      variant: 'outlined',
      size: 'small',
      fullWidth: true,
    };

    if (field.key === 'department') {
      return (
        <TextField
          key={field.key}
          {...commonProps}
          select
          value={rosterForm.department}
          onChange={(e) => handleDepartmentChange(e.target.value)}
        >
          <MenuItem value="">
            <em>Select Department</em>
          </MenuItem>
          {departmentOptions.map((d) => (
            <MenuItem key={d._id} value={d.name}>
              {d.name}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (field.key === 'city') {
      return (
        <TextField
          key={field.key}
          {...commonProps}
          select
          value={rosterForm.city}
          onChange={(e) => handleCityChange(e.target.value)}
        >
          <MenuItem value="">
            <em>Select City</em>
          </MenuItem>
          {cityOptions.map((c) => (
            <MenuItem key={c._id || c.name} value={c.name}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (field.key === 'ec') {
      const selectedCityName = String(rosterForm.city || '').trim();
      const filteredExperienceCenters = selectedCityName
        ? allExperienceCenterOptions.filter(
            (ec) => getEcCityName(ec) === selectedCityName
          )
        : [];
      const currentEcName = String(rosterForm.ec || '').trim();
      const ecNames = filteredExperienceCenters.map((ec) => ec.name);
      const savedEcNotInList =
        currentEcName && !ecNames.includes(currentEcName);

      return (
        <TextField
          key={field.key}
          {...commonProps}
          select
          disabled={!selectedCityName}
          helperText={!selectedCityName ? 'Select a city first' : ''}
          value={rosterForm.ec}
          onChange={(e) => handleFieldChange('ec', e.target.value)}
        >
          <MenuItem value="">
            <em>Select Experience Center</em>
          </MenuItem>
          {savedEcNotInList && (
            <MenuItem value={currentEcName}>
              {currentEcName} (current)
            </MenuItem>
          )}
          {filteredExperienceCenters.map((ec) => (
            <MenuItem key={ec._id || ec.name} value={ec.name}>
              {ec.name}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (field.key === 'role' || /^l[1-5]ManagerDesignation$/.test(field.key)) {
      const fieldKey = field.key;
      const value = fieldKey === 'role' ? rosterForm.role : rosterForm[fieldKey] || '';
      const selectedDeptName = String(rosterForm.department || '').trim();
      const filteredRoles =
        fieldKey === 'role' && selectedDeptName
          ? roleOptions.filter((r) => getRoleDepartmentName(r) === selectedDeptName)
          : roleOptions;
      const roleNames = filteredRoles.map((r) => r.name);
      const savedNotInList =
        fieldKey !== 'role' &&
        String(value).trim() &&
        !roleNames.includes(String(value).trim());

      return (
        <TextField
          key={fieldKey}
          {...commonProps}
          select
          disabled={fieldKey === 'role' && !selectedDeptName}
          helperText={
            fieldKey === 'role' && !selectedDeptName ? 'Select a department first' : ''
          }
          value={value}
          onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
        >
          <MenuItem value="">
            <em>Select Role</em>
          </MenuItem>
          {savedNotInList && (
            <MenuItem value={String(value).trim()}>
              {String(value).trim()} (current)
            </MenuItem>
          )}
          {filteredRoles.map((role) => (
            <MenuItem key={`${fieldKey}-${role._id}`} value={role.name}>
              {role.name}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (field.key === 'status') {
      const showJoiningDate = !isRosterOpenReqStatus(rosterForm.status);
      return (
        <React.Fragment key="status-fields">
          <TextField
            key="status"
            {...commonProps}
            select
            value={resolveRosterStatusSelectValue(
              rosterForm.status,
              hrStatusOptionsForForm
            )}
            onChange={(e) => handleStatusChange(e.target.value)}
          >
            <MenuItem value="">
              <em>Select Status</em>
            </MenuItem>
            {hrStatusOptionsForForm.map((s) => (
              <MenuItem key={s._id || s.status} value={s.status}>
                {s.status}
              </MenuItem>
            ))}
          </TextField>
          {needsStatusDate ? (
            <TextField
              key={statusDateFieldKey}
              label={`${getRosterStatusDateLabel(rosterForm.status)} *`}
              variant="outlined"
              size="small"
              fullWidth
              type="date"
              value={rosterForm[statusDateFieldKey] || ''}
              onChange={(e) => handleFieldChange(statusDateFieldKey, e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          ) : null}
          {showJoiningDate ? (
            <TextField
              key="joiningDate"
              label="Joining Date"
              variant="outlined"
              size="small"
              fullWidth
              type="date"
              value={rosterForm.joiningDate}
              onChange={(e) => handleFieldChange('joiningDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          ) : null}
        </React.Fragment>
      );
    }

    return (
      <TextField
        key={field.key}
        {...commonProps}
        type={field.type || 'text'}
        value={rosterForm[field.key]}
        onChange={(e) => handleFieldChange(field.key, e.target.value)}
        InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
      />
    );
  };

  const validateRosterForm = () => {
    const normalizedStatus = String(rosterForm.status || '').trim().toLowerCase();
    const isOpenReq = normalizedStatus === 'open req';
    const isNameOptional = isRosterNameOptionalStatus(normalizedStatus);

    const requiredFields = [
      { key: 'department', label: 'Department' },
      { key: 'city', label: 'City' },
      { key: 'ec', label: 'EC' },
      { key: 'role', label: 'Role' },
      { key: 'status', label: 'Status' },
      { key: 'l1ManagerName', label: 'L1 Manager Name' },
      { key: 'l1ManagerEmailId', label: 'L1 Manager Email ID' },
      { key: 'l1ManagerDesignation', label: 'L1 Manager Role' },
    ];

    if (!isOpenReq) {
      requiredFields.push({ key: 'emailId', label: 'Email ID' });
    }
    if (!isNameOptional) {
      requiredFields.push({ key: 'name', label: 'Name' });
    }
    if (isOpenReq) {
      requiredFields.push({ key: 'recruiter', label: 'Recruiter Name' });
    }

    const missingField = requiredFields.find(
      (field) => !String(rosterForm[field.key] || '').trim()
    );

    if (missingField) {
      return `${missingField.label} is required`;
    }

    const statusDateFieldKey = getRosterStatusDateFieldKey(normalizedStatus);
    if (statusDateFieldKey && !String(rosterForm[statusDateFieldKey] || '').trim()) {
      return `${getRosterStatusDateLabel(rosterForm.status)} is required`;
    }

    const basicEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (
      !isOpenReq &&
      rosterForm.emailId &&
      !basicEmailRegex.test(String(rosterForm.emailId).trim())
    ) {
      return 'Invalid Email ID';
    }

    return '';
  };

  const handleExcelUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    const isExcelFile =
      file.name.toLowerCase().endsWith('.xlsx') ||
      file.name.toLowerCase().endsWith('.xls');

    if (!isExcelFile) {
      props.setSnackInfo('Please upload a valid Excel file (.xlsx or .xls)', 'error');
      event.target.value = '';
      return;
    }

    const uploadCreatedBy = userDetails?._id || currentUserId;
    if (!uploadCreatedBy) {
      props.setSnackInfo('Unable to identify logged-in user. Please refresh and try again.', 'error');
      event.target.value = '';
      return;
    }

    props.setLoading(true);
    setUploading(true);
    try {
      await HttpService.uploadHrStatusColorCodeExcel(file, {
        createdBy: uploadCreatedBy,
      });
      props.setSnackInfo('Excel uploaded successfully', 'success');
    } catch (error) {
      console.error('Error uploading roster excel:', error);
      props.setSnackInfo(
        error?.response?.data?.message || 'Error uploading Excel file',
        'error'
      );
    } finally {
      props.setLoading(false);
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleOpenCreateModal = () => {
    setRosterForm(defaultRosterForm);
    setIsEditMode(false);
    setEditingRosterId('');
    setOpenCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setOpenCreateModal(false);
    setOpenDeactivateModal(false);
  };

  const handleOpenDeactivateConfirm = () => {
    if (!editingRosterId) return;
    setOpenDeactivateModal(true);
  };

  const handleDeactivateRoster = async () => {
    if (!editingRosterId) return;

    props.setLoading(true);
    try {
      const response = await HttpService.deactivateRoster(editingRosterId, {
        updatedBy: currentUserId,
      });
      props.setSnackInfo(
        response?.data?.message || 'Roster deactivated successfully',
        'success'
      );
      setOpenDeactivateModal(false);
      setOpenCreateModal(false);
      setIsEditMode(false);
      setEditingRosterId('');
      setRosterForm(defaultRosterForm);
      await fetchRosterList();
    } catch (error) {
      console.error('Error deactivating roster:', error);
      props.setSnackInfo(
        error?.response?.data?.message || 'Error deactivating roster',
        'error'
      );
    } finally {
      props.setLoading(false);
    }
  };

  const isRosterDeactiveStatus = (status) =>
    String(status || '').trim().toLowerCase() === 'deactive';

  const handleCreateUser = async () => {
    const validationMessage = validateRosterForm();
    if (validationMessage) {
      props.setSnackInfo(validationMessage, 'error');
      return;
    }

    props.setLoading(true);
    try {
      const payload = {
        ...rosterForm,
        ...(isEditMode
          ? { updatedBy: currentUserId || rosterForm.updatedBy || undefined }
          : { createdBy: currentUserId || rosterForm.createdBy || undefined }),
      };
      if (isEditMode && editingRosterId) {
        await HttpService.updateRoster(editingRosterId, payload);
        props.setSnackInfo('Roster updated successfully', 'success');
      } else {
        await HttpService.createRoster(payload);
        props.setSnackInfo('Roster created successfully', 'success');
      }
      setOpenCreateModal(false);
      setRosterForm(defaultRosterForm);
      setIsEditMode(false);
      setEditingRosterId('');
      await fetchRosterList();
    } catch (error) {
      console.error('Error saving roster:', error);
      const errorMessageFromErrorsArray = Array.isArray(error?.response?.data?.errors)
        ? error.response.data.errors[0]
        : '';
      props.setSnackInfo(
        errorMessageFromErrorsArray ||
          error?.response?.data?.message ||
          'Error saving roster',
        'error'
      );
    } finally {
      props.setLoading(false);
    }
  };

  const handleOpenEditModal = (item) => {
    setIsEditMode(true);
    setEditingRosterId(item?._id || '');
    setRosterForm({
      department: item?.department || '',
      city: item?.city || '',
      ec: item?.ec || '',
      role: item?.role || '',
      empId: item?.empId || '',
      name: item?.name || '',
      emailId: item?.emailId || '',
      joiningDate: formatRosterDateForInput(item?.joiningDate),
      ...ROSTER_STATUS_DATE_FIELD_KEYS.reduce((acc, key) => {
        acc[key] = formatRosterDateForInput(item?.[key]);
        return acc;
      }, {}),
      status: item?.status || '',
      recruiter: item?.recruiter || item?.recruiterName || '',
      statusNote: item?.statusNote || '',
      needsReview: item?.needsReview !== undefined ? String(item.needsReview) : '',
      l1ManagerName: item?.l1ManagerName || '',
      l1ManagerEmailId: item?.l1ManagerEmailId || '',
      l1ManagerDesignation: item?.l1ManagerDesignation || '',
      l2ManagerName: item?.l2ManagerName || '',
      l2ManagerEmailId: item?.l2ManagerEmailId || '',
      l2ManagerDesignation: item?.l2ManagerDesignation || '',
      l3ManagerName: item?.l3ManagerName || '',
      l3ManagerEmailId: item?.l3ManagerEmailId || '',
      l3ManagerDesignation: item?.l3ManagerDesignation || '',
      l4ManagerName: item?.l4ManagerName || '',
      l4ManagerEmailId: item?.l4ManagerEmailId || '',
      l4ManagerDesignation: item?.l4ManagerDesignation || '',
      l5ManagerName: item?.l5ManagerName || '',
      l5ManagerEmailId: item?.l5ManagerEmailId || '',
      l5ManagerDesignation: item?.l5ManagerDesignation || '',
    });
    setOpenCreateModal(true);
  };

  const handleOpenDeleteModal = (id) => {
    setDeletingRosterId(id);
    setOpenDeleteModal(true);
  };

  const handleDeleteRoster = async () => {
    if (!deletingRosterId) return;
    props.setLoading(true);
    try {
      await HttpService.deleteRoster(deletingRosterId);
      props.setSnackInfo('Roster deleted successfully', 'success');
      setOpenDeleteModal(false);
      setDeletingRosterId('');
      await fetchRosterList();
    } catch (error) {
      console.error('Error deleting roster:', error);
      props.setSnackInfo(
        error?.response?.data?.message || 'Error deleting roster',
        'error'
      );
    } finally {
      props.setLoading(false);
    }
  };

  const hexToRgba = (hex, alpha = 0.28) => {
    const h = String(hex || '').trim();
    if (!/^#[0-9a-fA-F]{6}$/.test(h)) return undefined;
    const n = parseInt(h.slice(1), 16);
    const r = (n >> 16) & 255;
    const g = (n >> 8) & 255;
    const b = n & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const getStatusRowBackground = (statusText) => {
    const key = String(statusText || '').trim().toLowerCase();
    if (!key) return undefined;
    const hit = hrStatusOptions.find(
      (h) => String(h.status || '').trim().toLowerCase() === key
    );
    if (!hit?.hexCode) return undefined;
    return hexToRgba(hit.hexCode, 0.32) || hit.hexCode;
  };

  return (
    <Box p={2}>
      <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 16 }}>
        Roster
      </Typography>

      <Box mb={2} display="flex" gridGap={12}>
        <input
          accept=".xlsx,.xls"
          id="roster-upload-input"
          type="file"
          style={{ display: 'none' }}
          onChange={handleExcelUpload}
        />
        <label htmlFor="roster-upload-input">
          <Button
            component="span"
            variant="contained"
            style={{ backgroundColor: '#F47E46', color: '#fff' }}
            disabled={uploading || !currentUserId}
          >
            {uploading ? 'Uploading...' : 'Upload Excel'}
          </Button>
        </label>
        <Button
          variant="outlined"
          startIcon={<GetAppIcon />}
          onClick={downloadRosterExcelTemplate}
          style={{ borderColor: '#F47E46', color: '#F47E46' }}
        >
          Download Template
        </Button>
        <Button
          variant="contained"
          style={{ backgroundColor: '#F47E46', color: '#fff' }}
          onClick={handleOpenCreateModal}
        >
          Create User
        </Button>
        <Button
          variant="outlined"
          onClick={() => props.history.push('/hr-racksheets-activity-log')}
          style={{ borderColor: '#F47E46', color: '#F47E46' }}
        >
          Logs
        </Button>
      </Box>

      <Box
        component="form"
        mb={2}
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        gridGap={12}
        onSubmit={handleFilterSubmit}
      >
        {ROSTER_FILTER_FIELDS.map((f) => (
          <TextField
            key={f.key}
            label={f.label}
            variant="outlined"
            size="small"
            value={filterInputs[f.key]}
            onChange={handleFilterInputChange(f.key)}
            placeholder={f.placeholder}
          />
        ))}
        <TextField
          select
          label="Department"
          variant="outlined"
          size="small"
          style={{ minWidth: 160 }}
          value={filterInputs.department}
          onChange={handleFilterInputChange('department')}
        >
          <MenuItem value="">
            <em>All Departments</em>
          </MenuItem>
          {departmentOptions.map((d) => (
            <MenuItem key={d._id || d.name} value={d.name}>
              {d.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="City"
          variant="outlined"
          size="small"
          style={{ minWidth: 160 }}
          value={filterInputs.city}
          onChange={handleFilterInputChange('city')}
        >
          <MenuItem value="">
            <em>All Cities</em>
          </MenuItem>
          {cityOptions.map((c) => (
            <MenuItem key={c._id || c.name} value={c.name}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="EC"
          variant="outlined"
          size="small"
          style={{ minWidth: 160 }}
          value={filterInputs.ec}
          onChange={handleFilterInputChange('ec')}
        >
          <MenuItem value="">
            <em>All ECs</em>
          </MenuItem>
          {allExperienceCenterOptions.map((ec) => (
            <MenuItem key={ec._id || ec.name} value={ec.name}>
              {ec.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Role"
          variant="outlined"
          size="small"
          style={{ minWidth: 160 }}
          value={filterInputs.role}
          onChange={handleFilterInputChange('role')}
        >
          <MenuItem value="">
            <em>All Roles</em>
          </MenuItem>
          {roleOptions.map((r) => (
            <MenuItem key={r._id || r.name} value={r.name}>
              {r.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Status"
          variant="outlined"
          size="small"
          style={{ minWidth: 160 }}
          value={filterInputs.status}
          onChange={handleFilterInputChange('status')}
        >
          <MenuItem value="">
            <em>All Statuses</em>
          </MenuItem>
          {hrStatusOptions.map((h) => (
            <MenuItem key={h._id || h.status} value={h.status}>
              {h.status}
            </MenuItem>
          ))}
        </TextField>
        <Button
          type="submit"
          variant="contained"
          style={{ backgroundColor: '#F47E46', color: '#fff' }}
        >
          Search
        </Button>
        <Button variant="outlined" onClick={handleClearFilters}>
          Clear
        </Button>
      </Box>

      <TablePagination
        component="div"
        rowsPerPageOptions={ROSTER_PAGE_SIZE_OPTIONS}
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={Math.max(0, page - 1)}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <TableContainer
        component={Paper}
        style={{ border: ROSTER_CELL_BORDER, borderColor: ROSTER_CELL_BORDER_COLOR, borderRadius: 0 }}
      >
        <Table size="small" style={{ borderCollapse: 'collapse' }}>
          <TableHead>
            <TableRow>
              <TableCell style={rosterTableHeaderCell}>Sr. No.</TableCell>
              <TableCell style={rosterTableHeaderCell}>EMP ID</TableCell>
              <TableCell style={rosterTableHeaderCell}>Name</TableCell>
              <TableCell style={rosterTableHeaderCell}>Email ID</TableCell>
              <TableCell style={rosterTableHeaderCell}>Role</TableCell>
              <TableCell style={rosterTableHeaderCell}>Department</TableCell>
              <TableCell style={rosterTableHeaderCell}>City</TableCell>
              <TableCell style={rosterTableHeaderCell}>EC</TableCell>
              <TableCell style={rosterTableHeaderCell}>Joining Date</TableCell>
              <TableCell style={rosterTableHeaderCell}>Status Date</TableCell>
              <TableCell style={rosterTableHeaderCell}>Status</TableCell>
              <TableCell style={rosterTableHeaderCell}>Status Note</TableCell>
              <TableCell style={rosterTableHeaderCell}>Recruiter Name</TableCell>
              <TableCell style={rosterTableHeaderCell}>Needs Review</TableCell>
              <TableCell style={rosterTableHeaderCell}>L1 Manager Name</TableCell>
              <TableCell style={rosterTableHeaderCell}>L1 Manager Email ID</TableCell>
              <TableCell style={rosterTableHeaderCell}>L1 Manager Role</TableCell>
              <TableCell style={rosterTableHeaderCell}>L2 Manager Name</TableCell>
              <TableCell style={rosterTableHeaderCell}>L2 Manager Email ID</TableCell>
              <TableCell style={rosterTableHeaderCell}>L2 Manager Role</TableCell>
              <TableCell style={rosterTableHeaderCell}>L3 Manager Name</TableCell>
              <TableCell style={rosterTableHeaderCell}>L3 Manager Email ID</TableCell>
              <TableCell style={rosterTableHeaderCell}>L3 Manager Role</TableCell>
              <TableCell style={rosterTableHeaderCell}>L4 Manager Name</TableCell>
              <TableCell style={rosterTableHeaderCell}>L4 Manager Email ID</TableCell>
              <TableCell style={rosterTableHeaderCell}>L4 Manager Role</TableCell>
              <TableCell style={rosterTableHeaderCell}>L5 Manager Name</TableCell>
              <TableCell style={rosterTableHeaderCell}>L5 Manager Email ID</TableCell>
              <TableCell style={rosterTableHeaderCell}>L5 Manager Role</TableCell>
              <TableCell style={rosterTableHeaderCell}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={31} align="center" style={rosterTableBodyCell}>
                  No roster records found
                </TableCell>
              </TableRow>
            ) : (
              displayRows.map((item, index) => (
                <TableRow
                  key={item?._id}
                  style={{
                    backgroundColor: getStatusRowBackground(item?.status),
                  }}
                >
                  <TableCell style={rosterTableBodyCell}>
                    {(page - 1) * rowsPerPage + index + 1}
                  </TableCell>
                  <TableCell style={rosterTableBodyCell}>
                    {formatEmpIdForDisplay(item?.empId)}
                  </TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.name || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.emailId || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.role || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.department || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.city || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.ec || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>
                    {item?.joiningDate ? formatRosterDateForDisplay(item.joiningDate) : '-'}
                  </TableCell>
                  <TableCell style={rosterTableBodyCell}>
                    {(() => {
                      const statusDate = resolveRosterStatusDateFromRow(item);
                      return statusDate ? formatRosterDateForDisplay(statusDate) : '-';
                    })()}
                  </TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.status || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.statusNote || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>
                    {item?.recruiter || item?.recruiterName || '-'}
                  </TableCell>
                  <TableCell style={rosterTableBodyCell}>
                    {item?.needsReview === true
                      ? 'Yes'
                      : item?.needsReview === false
                        ? 'No'
                        : item?.needsReview || '-'}
                  </TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.l1ManagerName || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.l1ManagerEmailId || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.l1ManagerDesignation || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.l2ManagerName || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.l2ManagerEmailId || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.l2ManagerDesignation || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.l3ManagerName || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.l3ManagerEmailId || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.l3ManagerDesignation || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.l4ManagerName || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.l4ManagerEmailId || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.l4ManagerDesignation || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.l5ManagerName || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.l5ManagerEmailId || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>{item?.l5ManagerDesignation || '-'}</TableCell>
                  <TableCell style={rosterTableBodyCell}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenEditModal(item)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        style={{ color: '#d32f2f' }}
                        onClick={() => handleOpenDeleteModal(item?._id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openCreateModal} onClose={handleCloseCreateModal} fullWidth maxWidth="md">
        <DialogTitle>{isEditMode ? 'Update User' : 'Create User'}</DialogTitle>
        <DialogContent>
          <Box
            mt={1}
            display="grid"
            gridTemplateColumns="repeat(2, minmax(260px, 1fr))"
            gridGap={16}
          >
            {rosterFields.map((field) => renderRosterFormField(field))}
          </Box>

        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateModal} color="default">
            Cancel
          </Button>
          <Button
            onClick={handleCreateUser}
            color="primary"
            variant="contained"
            style={{ backgroundColor: '#F47E46', color: '#fff' }}
          >
            {isEditMode ? 'Update User' : 'Create User'}
          </Button>
          {isEditMode && !isRosterDeactiveStatus(rosterForm.status) ? (
            <Button
              onClick={handleOpenDeactivateConfirm}
              color="secondary"
              variant="outlined"
              style={{ borderColor: '#d32f2f', color: '#d32f2f' }}
            >
              Deactivate
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeactivateModal}
        onClose={() => setOpenDeactivateModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Deactivate Roster Record</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate this roster record? The status will be set to
            Deactive.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeactivateModal(false)} color="default">
            Cancel
          </Button>
          <Button onClick={handleDeactivateRoster} color="secondary" variant="contained">
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Roster Record</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this roster record?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteModal(false)} color="default">
            Cancel
          </Button>
          <Button onClick={handleDeleteRoster} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default connect(null, { setLoading, setSnackInfo })(Roster);